import { useEffect, useState } from "react";
import { Fleet } from "../lib/api";
import { Fuel, Plus, Trash2, Route, Gauge, Banknote, Activity } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmProvider";

const EMPTY_FUEL = {
  date: new Date().toISOString().slice(0, 10),
  liters: "",
  cost_pln: "",
  mileage_km: "",
  notes: "",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtDuration(mins) {
  if (mins == null) return "—";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h === 0) return `${m} min`;
  return `${h} h ${m} min`;
}

export default function VehicleAnalytics({ vehicleId }) {
  const { confirm } = useConfirm();
  const [stats, setStats] = useState(null);
  const [trips, setTrips] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [fuelForm, setFuelForm] = useState(EMPTY_FUEL);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t, f] = await Promise.all([
        Fleet.stats(vehicleId),
        Fleet.trips(vehicleId),
        Fleet.fuelList(vehicleId),
      ]);
      setStats(s);
      setTrips(t.trips || []);
      setFuels(f);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [vehicleId]);

  const addFuel = async (e) => {
    e.preventDefault();
    if (!fuelForm.liters || !fuelForm.cost_pln) {
      toast.error("Podaj litry i koszt");
      return;
    }
    try {
      await Fleet.addFuel(vehicleId, {
        date: fuelForm.date,
        liters: Number(fuelForm.liters),
        cost_pln: Number(fuelForm.cost_pln),
        mileage_km: fuelForm.mileage_km === "" ? null : Number(fuelForm.mileage_km),
        notes: fuelForm.notes,
      });
      setFuelForm(EMPTY_FUEL);
      toast.success("Tankowanie dodane");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const removeFuel = async (id) => {
    if (!(await confirm("Usunąć ten wpis?", { tone: "danger", confirmLabel: "Usuń" }))) return;
    try {
      await Fleet.removeFuel(vehicleId, id);
      toast.success("Usunięto");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-zinc-500 text-sm">Ładowanie statystyk...</div>;
  }

  return (
    <div className="space-y-10" data-testid="vehicle-analytics">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-zinc-900">
        <Kpi
          icon={Gauge}
          label="Przebieg GPS"
          value={`${(stats?.total_km ?? 0).toFixed(1)} km`}
          testId="kpi-total-km"
        />
        <Kpi
          icon={Fuel}
          label="Tankowania"
          value={`${(stats?.total_liters ?? 0).toFixed(1)} L`}
          sub={`${stats?.refuels_count || 0} wpisów`}
          dark
          testId="kpi-total-fuel"
        />
        <Kpi
          icon={Banknote}
          label="Koszt paliwa"
          value={`${(stats?.total_fuel_cost_pln ?? 0).toFixed(2)} zł`}
          testId="kpi-total-fuel-cost"
        />
        <Kpi
          icon={Activity}
          label="Średnie spalanie"
          value={(() => {
            const v = stats?.avg_consumption_l_100km;
            if (v == null) return "—";
            if (v > 50) return "—";
            return `${v} L/100km`;
          })()}
          sub={
            stats?.avg_consumption_l_100km != null && stats.avg_consumption_l_100km > 50
              ? "zbyt mało km GPS"
              : "GPS + tankowania"
          }
          dark
          testId="kpi-avg-consumption"
        />
      </div>

      {/* Trips */}
      <section className="space-y-4" data-testid="trips-section">
        <div>
          <div className="font-label mb-1">// Telematyka</div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight flex items-center gap-3">
            <Route className="w-6 h-6 text-brand" /> Historia tras ({trips.length})
          </h2>
        </div>
        <div className="border border-zinc-900 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-900 text-white text-left">
                <Th>Start</Th>
                <Th>Koniec</Th>
                <Th>Dystans</Th>
                <Th>Czas</Th>
                <Th>Max prędkość</Th>
                <Th>Punkty</Th>
              </tr>
            </thead>
            <tbody>
              {trips.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-zinc-500 text-sm">
                    Brak tras — symulator dopiero startuje. Trasa zostanie wykryta po kilku minutach jazdy.
                  </td>
                </tr>
              )}
              {trips.map((t, i) => (
                <tr
                  key={i}
                  className="border-t border-zinc-200 hover:bg-zinc-50"
                  data-testid={`trip-row-${i}`}
                >
                  <td className="py-3 px-4 font-mono text-xs">{fmtDateTime(t.start_time)}</td>
                  <td className="py-3 px-4 font-mono text-xs">{fmtDateTime(t.end_time)}</td>
                  <td className="py-3 px-4 font-mono text-sm font-bold">{t.distance_km} km</td>
                  <td className="py-3 px-4 font-mono text-xs">{fmtDuration(t.duration_min)}</td>
                  <td className="py-3 px-4 font-mono text-xs">{t.max_speed_kmh} km/h</td>
                  <td className="py-3 px-4 font-mono text-xs text-zinc-500">{t.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Fuel */}
      <section className="space-y-4" data-testid="fuel-section">
        <div>
          <div className="font-label mb-1">// Ewidencja</div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight flex items-center gap-3">
            <Fuel className="w-6 h-6 text-brand" /> Tankowania ({fuels.length})
          </h2>
        </div>

        <form
          onSubmit={addFuel}
          data-testid="add-fuel-form"
          className="border border-zinc-900 p-6 grid grid-cols-1 md:grid-cols-12 gap-4"
        >
          <div className="md:col-span-2">
            <span className="font-label block mb-2">Data</span>
            <input
              type="date"
              data-testid="fuel-input-date"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
              value={fuelForm.date}
              onChange={(e) => setFuelForm({ ...fuelForm, date: e.target.value })}
              required
            />
          </div>
          <div className="md:col-span-2">
            <span className="font-label block mb-2">Litry *</span>
            <input
              type="number"
              step="0.01"
              data-testid="fuel-input-liters"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
              value={fuelForm.liters}
              onChange={(e) => setFuelForm({ ...fuelForm, liters: e.target.value })}
              required
            />
          </div>
          <div className="md:col-span-2">
            <span className="font-label block mb-2">Koszt (zł) *</span>
            <input
              type="number"
              step="0.01"
              data-testid="fuel-input-cost"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
              value={fuelForm.cost_pln}
              onChange={(e) => setFuelForm({ ...fuelForm, cost_pln: e.target.value })}
              required
            />
          </div>
          <div className="md:col-span-2">
            <span className="font-label block mb-2">Przebieg (km)</span>
            <input
              type="number"
              data-testid="fuel-input-mileage"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
              value={fuelForm.mileage_km}
              onChange={(e) => setFuelForm({ ...fuelForm, mileage_km: e.target.value })}
            />
          </div>
          <div className="md:col-span-3">
            <span className="font-label block mb-2">Uwagi (stacja, typ paliwa…)</span>
            <input
              type="text"
              data-testid="fuel-input-notes"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
              value={fuelForm.notes}
              onChange={(e) => setFuelForm({ ...fuelForm, notes: e.target.value })}
            />
          </div>
          <div className="md:col-span-1 flex items-end">
            <button
              type="submit"
              data-testid="btn-add-fuel"
              className="w-full inline-flex items-center justify-center gap-1 bg-brand text-white px-3 py-2 font-bold uppercase tracking-wider hover:bg-brand-hover"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </form>

        <div className="border border-zinc-900 overflow-x-auto">
          <table className="w-full" data-testid="fuel-table">
            <thead>
              <tr className="bg-zinc-900 text-white text-left">
                <Th>Data</Th>
                <Th>Litry</Th>
                <Th>Koszt</Th>
                <Th>Cena/L</Th>
                <Th>Przebieg</Th>
                <Th>Uwagi</Th>
                <Th className="text-right">Akcje</Th>
              </tr>
            </thead>
            <tbody>
              {fuels.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-12 text-center text-zinc-500 text-sm"
                    data-testid="empty-fuel"
                  >
                    Brak tankowań. Dodaj pierwsze powyżej.
                  </td>
                </tr>
              )}
              {fuels.map((f) => {
                const pricePerL = f.liters > 0 ? (f.cost_pln / f.liters).toFixed(2) : "—";
                return (
                  <tr
                    key={f.id}
                    className="border-t border-zinc-200 hover:bg-zinc-50"
                    data-testid={`fuel-row-${f.id}`}
                  >
                    <td className="py-3 px-4 font-mono text-sm">
                      {new Date(f.date).toLocaleDateString("pl-PL")}
                    </td>
                    <td className="py-3 px-4 font-mono text-sm">
                      {f.liters.toFixed(2)} L
                    </td>
                    <td className="py-3 px-4 font-mono text-sm font-semibold">
                      {f.cost_pln.toFixed(2)} zł
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-zinc-500">
                      {pricePerL} zł/L
                    </td>
                    <td className="py-3 px-4 font-mono text-sm">
                      {f.mileage_km ? `${f.mileage_km.toLocaleString("pl-PL")} km` : "—"}
                    </td>
                    <td className="py-3 px-4 text-xs text-zinc-600">{f.notes || "—"}</td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => removeFuel(f.id)}
                        data-testid={`btn-delete-fuel-${f.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-red-600 hover:border-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, dark, testId }) {
  return (
    <div
      data-testid={testId}
      className={`p-5 border-r border-zinc-900 last:border-r-0 ${
        dark ? "bg-zinc-900 text-white" : "bg-white text-zinc-900"
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`font-label ${dark ? "text-zinc-400" : "text-zinc-500"}`}>
          {label}
        </div>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      <div className="font-heading font-black text-2xl">{value}</div>
      {sub && (
        <div className={`text-[11px] font-mono mt-1 ${dark ? "text-zinc-400" : "text-zinc-500"}`}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`py-3 px-4 text-xs uppercase tracking-[0.2em] font-bold ${className}`}>
      {children}
    </th>
  );
}
